import React, { useState } from 'react';
import { Card, Button, Alert, Collapse, Badge } from 'react-bootstrap';
import { CreditCard, Edit, Clock, AlertTriangle, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import moment from 'moment';
import UpdateBankAccountModal from './UpdateBankAccountModal';

const BankAccountCard = ({ employeeId, companyId, bankAccount, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  if (!bankAccount) {
    return (
      <>
      <div className="info-section mb-4" role="region" aria-labelledby="bank-account-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <CreditCard size={20} className="me-3 text-info" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="bank-account-heading">Bankkonto</h5>
                <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                  Inget bankkonto registrerat
                </p>
              </div>
            </div>
          </div>
          <div className="section-divider"></div>
          <div className="text-center py-5">
            <div className="mb-4">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: '80px', height: '80px' }}>
                <CreditCard size={40} className="text-info" aria-hidden="true" />
              </div>
            </div>
            <h5 className="mb-2">Inget bankkonto har registrerats än</h5>
            <p className="text-muted mb-4">
              Lägg till bankuppgifter för att kunna betala ut lön till denna anställd.
            </p>
            <Button
              variant="primary"
              size="lg"
              className="action-button d-flex align-items-center mx-auto"
              onClick={handleCreateClick}
              aria-label="Lägg till bankkonto för anställd"
            >
              <Edit size={18} className="me-2" aria-hidden="true" />
              Lägg till bankkonto nu
            </Button>
          </div>
        </div>
      </div>
      <UpdateBankAccountModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentBankAccount={modalMode === 'edit' ? bankAccount : null}
        currentContract={currentContract}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
      </>
    );
  }

  return (
    <>
      <div className="info-section mb-4" role="region" aria-labelledby="bank-account-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <CreditCard size={20} className="me-3 text-info" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="bank-account-heading">
                  {bankAccount._isFuture ? 'Kommande bankkonto' : 'Bankkonto'}
                </h5>
                {bankAccount.valid_from && (
                  <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                    {bankAccount._isFuture ? 'Börjar' : 'Gäller från'} {moment(bankAccount.valid_from).format('DD MMM YYYY')}
                  </p>
                )}
              </div>
              {bankAccount._isFuture && (
                <span className="badge bg-primary ms-2" style={{ fontSize: '0.75rem' }}>
                  Kommande
                </span>
              )}
            </div>
            <Button
              variant="primary"
              className="action-button d-flex align-items-center"
              onClick={handleCreateClick}
              aria-label="Skapa nytt bankkonto för anställd"
            >
              <Edit size={16} className="me-2" aria-hidden="true" />
              Skapa nytt konto
            </Button>
          </div>
          <div className="section-divider"></div>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera bankkonto"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Clearingnummer</div>
                <div className="h5 mb-0 fw-semibold">
                  {bankAccount.clearing_no || '-'}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera bankkonto"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Kontonummer</div>
                <div className="h5 mb-0 fw-semibold">
                  {bankAccount.account_no || '-'}
                </div>
              </div>
            </div>
            {(bankAccount.iban || bankAccount.bic) && (
              <>
                <div className="col-md-6">
                  <div className="p-3 border rounded-lg bg-light">
                    <div className="text-muted small mb-1">IBAN</div>
                    <div className="h6 mb-0">
                      {bankAccount.iban || '-'}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded-lg bg-light">
                    <div className="text-muted small mb-1">BIC/SWIFT</div>
                    <div className="h6 mb-0">
                      {bankAccount.bic || '-'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* History Toggle */}
          {history && history.length > 1 && (
            <div className="mt-3">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="d-flex align-items-center p-0 text-decoration-none"
              >
                {showHistory ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {showHistory ? 'Dölj' : 'Visa'} kontohistorik ({history.length - 1} tidigare ändringar)
              </Button>

              <Collapse in={showHistory}>
                <div className="mt-3">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Clearingnummer</th>
                        <th>Kontonummer</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((account) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        const validFrom = new Date(account.valid_from);
                        validFrom.setHours(0, 0, 0, 0);

                        const validTo = account.valid_to ? new Date(account.valid_to) : null;
                        if (validTo) {
                          validTo.setHours(0, 0, 0, 0);
                        }

                        // Determine status based on date ranges
                        let status = 'previous';
                        let statusLabel = 'Tidigare';
                        let statusBg = 'secondary';

                        if (validFrom > today) {
                          // Future bank account (starts after today)
                          status = 'upcoming';
                          statusLabel = 'Kommande';
                          statusBg = 'info';
                        } else if (validFrom <= today && (!validTo || validTo >= today)) {
                          // Current bank account (started on/before today and hasn't ended yet)
                          status = 'current';
                          statusLabel = 'Aktuell';
                          statusBg = 'success';
                        }

                        const isCurrent = status === 'current';

                        return (
                          <tr key={account.bank_id} className={isCurrent ? 'table-active' : ''}>
                            <td>
                              {formatDate(account.valid_from)}
                              {account.valid_to ? ` - ${formatDate(account.valid_to)}` : ' - Pågående'}
                            </td>
                            <td>{account.clearing_no || '-'}</td>
                            <td>{account.account_no || '-'}</td>
                            <td>
                              <Badge bg={statusBg}>{statusLabel}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </div>

      <UpdateBankAccountModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentBankAccount={modalMode === 'edit' ? bankAccount : null}
        currentContract={currentContract}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default BankAccountCard;
